import axios from "axios";
import { BASE_URL } from "./baseUrls";

import {
  InterceptorRequestHandler,
  InterceptorResponseErrorHandler,
  InterceptorResponseHandler,
} from "./InterceptorHandler";
import { PRODUCT_API_URL } from "./urlMetaData";
import secureLocalStorage from "react-secure-storage";
import { AUTH_LOCAL_VARIABLES } from "../store/features/authSlice";

// get region value from local storage
const ProductService = axios.create({ baseURL: BASE_URL.PRODUCT });

// Service Interceptors Configuration
ProductService.interceptors.request.use((req) =>
  InterceptorRequestHandler(req)
);

// Dynamically, setting baseURL based on active region
ProductService.interceptors.request.use((config) => {
  const REGION = secureLocalStorage.getItem(AUTH_LOCAL_VARIABLES.REGION);
  if (!REGION) return config;
  config.baseURL = BASE_URL[REGION].PRODUCT;
  return config;
});

ProductService.interceptors.response.use(
  (res) => {
    InterceptorResponseHandler(res);
    return res;
  },
  (error) => {
    InterceptorResponseErrorHandler(error);
    return error;
  }
);

export const sendResetPasswordEmail = async ({ email }) => {
  return await ProductService.post(PRODUCT_API_URL.FORGOT_PASSWORD, {
    userName: email,
  });
};

export const resetPassword = async ({
  userName,
  newPassword,
  confirmNewPassword,
}) => {
  return await ProductService.post(PRODUCT_API_URL.RESET_PASSWORD, {
    userName,
    newPassword,
    confirmNewPassword,
  });
};

export const fetchSubUsersList = async ({
  userHashId,
  programCode,
  currencyCode,
  userType,
}) => {
  return await ProductService.post(PRODUCT_API_URL.GET_SUBUSERS_LIST, {
    userHashId,
    programCode,
    currencyCode,
    userType,
  });
};

export const fetchSupervisorUsersList = async ({ userHashId, userType }) => {
  return await ProductService.post(PRODUCT_API_URL.GET_SUPERVISOR_LIST, {
    userHashId,
    userType,
  });
};

export const fetchUsersList = async ({
  userHashId,
  clientHashId,
  page = 0,
  dateRange,
  size = 10,
  filters,
}) => {
  return await ProductService.post(PRODUCT_API_URL.GET_USERS_LIST, {
    onboardUserHashId: userHashId,
    clientHashId,
    dateRange,
    page,
    size,
    ...filters,
  });
};

export const UpdateUser = async (payload) => {
  return await ProductService.post(PRODUCT_API_URL.UPDATE_USER, payload);
};

export const UpdateUserStatus = async (payload) => {
  return await ProductService.post(PRODUCT_API_URL.UPDATE_USER_STATUS, payload);
};

export const inviteNewUser = async (payload) => {
  return await ProductService.post(PRODUCT_API_URL.ADD_NEW_USER, payload);
};

export const fetchUserUsageReport = async ({
  clientHashId,
  dateRange,
  page,
  size = 10,
}) => {
  return await ProductService.post(PRODUCT_API_URL.USAGE_REPORT, {
    clientHashId,
    dateRange,
    page,
    size,
  });
};

export const fetchClientProfile = async ({ clientHashId }) => {
  const res = await ProductService.get(
    `${PRODUCT_API_URL.GET_CLIENT}/${clientHashId}`
  );
  return res.data;
};

export const ChangePassword = async (payload) => {
  return await ProductService.post(PRODUCT_API_URL.CHANGE_PASSWORD, payload);
};

export const fetchTeamBalanceList = async ({
  userHashId,
  programCode,
  currencyCode,
}) => {
  const res = await ProductService.post(`${PRODUCT_API_URL.GET_TEAM_BALANCE}`, {
    userHashId,
    programCode,
    currencyCode,
  });
  return res.data;
};

export const fetchUserCountStats = async ({ userHashId, programCode }) => {
  const res = await ProductService.get(
    `${PRODUCT_API_URL.USER_COUNT}/${userHashId}`
  );
  return res.data;
};

export const CheckTokenStatus = async ({ userName }) => {
  const res = await ProductService.post(
    `${PRODUCT_API_URL.CHECK_TOKEN_STATUS}`,
    {
      userName,
    }
  );
  return res.data;
};

export default ProductService;
